import React from 'react';
import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  Cell,
  Text
} from 'recharts';
import { useTheme } from '@material-ui/core/styles';
import { radiusVertical } from 'utils/charts';

const dummyData = [
  { name: 'Risk requirement', value: 2 },
  { name: 'Risk tolerance', value: 5 },
  { name: 'Risk capacity', value: 1 },
  { name: 'Recommended risk profile', value: 7 }
];

interface RiskSummaryProps {
  data: Array<any>;
}
interface TickProps {
  x: number;
  y: number;
  payload: any;
  width: number;
}

const CustomizedAxisTick = ({ x, y, payload, width = 100 }: TickProps) => {
  return (
    <Text
      x={x}
      y={y + 20}
      width={width}
      textAnchor="middle"
      verticalAnchor="start"
      fontSize={12.8}
    >
      {payload.value}
    </Text>
  );
};

const RiskSummary = ({ data }: RiskSummaryProps) => {
  const theme = useTheme() as any;
  const [dataToRender, setDataToRender] = useState(dummyData);
  const isMoreThanOneCustomer = dataToRender.length > 4;
  const [initFire, setInitFire] = useState(false);

  const initData = () => {
    setTimeout(() => {
      setDataToRender(data);
      setInitFire(true);
    }, 50);
  };

  const updateData = () => {
    if (initFire) {
      setDataToRender(data);
    }
  };

  useEffect(() => {
    updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResponsiveContainer width="100%" height={255}>
      <BarChart
        data={dataToRender}
        margin={{
          top: 20,
          right: 20,
          left: 20,
          bottom: isMoreThanOneCustomer ? 20 : 25
        }}
        barCategoryGap={5}
      >
        <XAxis
          padding={{ left: 2, right: 2 }}
          dataKey="name"
          axisLine={false}
          interval={0}
          tickLine={false}
          tick={(props: TickProps) => (
            <CustomizedAxisTick
              x={props.x}
              y={props.y}
              payload={props.payload}
              width={isMoreThanOneCustomer ? 75 : 100}
            />
          )}
        />

        <Bar
          dataKey="value"
          radius={radiusVertical}
          label={({ x, y, width, value, height, fill, offset }: any) => {
            const isValueNegative = value < 0;
            return (
              <text
                style={{
                  fontSize: '13px'
                }}
                height={height}
                className="recharts-text recharts-label"
                x={x + width / 2}
                fill={fill}
                y={isValueNegative ? y + offset : y - offset}
                textAnchor="middle"
              >
                <tspan
                  x={x + width / 2}
                  dy={isValueNegative ? '0.71em' : '0em'}
                >
                  {value}
                </tspan>
              </text>
            );
          }}
          isAnimationActive={true}
        >
          {dataToRender.map((entry, index) => {
            const { value } = entry;
            const isNullValue = value === 0;
            return (
              <Cell
                y={215 - 18.5 * value}
                height={18.5 * value}
                key={`cell-${index}`}
                fill={
                  isNullValue
                    ? '#e2e9f0'
                    : theme.palette.baseColors.charts['risk-analysis'][index]
                }
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default RiskSummary;
